import { TeamMapLayers } from '../TeamMapLayers'
import { useMemo } from 'react'
import { GeoJsonFillLayer, GeoJsonLineLayer, MapMarker } from '@wehunt/map'
import type { AreaDisplayItem } from '../../lib/areaGrouping'
import {
  FORBIDDEN_FILL,
  FORBIDDEN_STROKE,
  GROUND_BASE_WIDTH,
  GROUND_DIM_FACTOR,
  GROUND_HALO_OPACITY,
  GROUND_HIGHLIGHT_COLOR,
  SUBAREA_STROKE,
  SUBAREA_STROKE_OPACITY,
  polygonCentroidOfRings,
  shouldDrawGroundHalo,
} from '../../lib/mapBorders'
import { groundHaloOffsetExpression, groundHitWidthExpression, groundWidthExpression } from '../../lib/mapGeoJson'
import type { GeoArea, GeoPt, TeamMap } from '../../lib/types'
import type { MapAppearance } from '../../lib/mapAppearance'
import {
  BORDER_HALO_ID,
  BORDER_HIGHLIGHT_ID,
  BORDER_LINE_ID,
  FORBIDDEN_FILL_ID,
  FORBIDDEN_HIGHLIGHT_ID,
  FORBIDDEN_LINE_ID,
  GROUND_HIT_ID,
  SUBAREA_HIGHLIGHT_ID,
  SUBAREA_LINE_ID,
} from './editorLayerIds'
import { groundDisplayFeatureCollection, groundHitFeatures, isAreaItemHidden } from './editorGroundGeoJson'
import type { LayerId } from './mapLibreEditorTypes'

/**
 * No real feature id can ever equal this, so a highlight filter with no
 * `selectedId` matches nothing rather than every feature whose `id` property
 * happens to be absent (MapLibre's `==` against `null`/`undefined` would
 * otherwise need its own branch).
 *
 * Namespaced and double-underscore-delimited by construction: every real id
 * this canvas's features carry is either a Firestore document id (opaque,
 * server-generated) or one of this app's own `genId(prefix)` ids
 * (`b…`/`sa…`/`fa…`, alphanumeric) — none of which can ever
 * equal this exact literal. A bare word like `'no-selection'` (what this
 * constant used to be, until a stray NUL byte landed in front of it and
 * turned the whole file into git-invisible "binary") is not structurally
 * guaranteed unique the same way; this sentinel's shape makes a collision a
 * naming-convention violation elsewhere, not a coincidence to hope against.
 */
const NO_SELECTION = '__wehunt-no-selection__'

/** Sub-area labels only show at or above this zoom — mirrors
 *  `FullscreenMapCanvas`'s own `tileZoom >= 10` gate. */
const SUBAREA_LABEL_MIN_ZOOM = 10

/** The label's dimmed opacity when the sub-areas layer isn't the active tab —
 *  `FullscreenMapCanvas`'s own fixed 0.55 (a text label doesn't run through
 *  `GROUND_DIM_FACTOR`, whose 0.4 read as barely-there against map tiles). */
const LABEL_DIM_OPACITY = 0.55

export interface EditorGroundLayersProps {
  printMap?: TeamMap
  dimOutside?: GeoPt[] | null
  borderDisplay: AreaDisplayItem<GeoArea>[]
  subAreaDisplay: AreaDisplayItem<GeoArea>[]
  forbiddenDisplay: AreaDisplayItem<GeoArea>[]
  activeTab: LayerId
  appearance: MapAppearance
  teamColor?: string
  selectedId: string | null
  /** Whether a border/sub-area/forbidden polygon may be clicked right now —
   *  `isPolygonSelectable({ drawing, vertexEditing, placeMode })`, computed
   *  once by the parent since it's the same boolean for all three kinds. */
  clickable: boolean
  /** Suppresses the border hit layer only — mirrors `FullscreenMapCanvas`'s
   *  `!showParcels` gate on the border hit TARGET alone (its crisp edge stays
   *  clickable throughout; only the wide invisible target steps aside so a
   *  parcel marker underneath it can be hit instead). */
  showParcels: boolean
  borderEditingId?: string
  borderVertexId?: string
  areaEditingId?: string
  subareaVertexId?: string
  forbiddenVertexId?: string
  /** Live zoom — gates sub-area labels. */
  zoom: number
}

/** Border/sub-area/forbidden rendering: visible core strokes (dimmed off the
 *  active tab per `GROUND_DIM_FACTOR`), the standard halo band under the border
 *  core, a `selectedId`-filtered highlight, and a combined wide hit layer for
 *  the click router. The reverse/filled washes are deliberately NOT drawn here:
 *  the app's planning map is standard-only as well, and a wash under the parcels
 *  being edited would hide the seams the editor exists to fix. */
export function EditorGroundLayers({
  borderDisplay,
  subAreaDisplay,
  forbiddenDisplay,
  activeTab,
  appearance,
  teamColor,
  selectedId,
  clickable,
  showParcels,
  borderEditingId,
  borderVertexId,
  areaEditingId,
  subareaVertexId,
  forbiddenVertexId,
  zoom,
  printMap,
  dimOutside,
}: EditorGroundLayersProps) {
  const lt = appearance.lineThickness
  const borderDim = activeTab !== 'border'
  const subareaDim = activeTab !== 'subareas'
  const forbiddenDim = activeTab !== 'forbidden'
  // CLUB-667: the editor honours the appearance panel's sub-area switch, so
  // one set of view settings holds across the map, the print sheet and here.
  // The exception is the Sub-areas tool itself — the layer you are editing is
  // always drawn, because a tool that operates on an invisible layer is just a
  // broken tool.
  const subareasShown = appearance.subAreasVisible || activeTab === 'subareas'
  // The forbidden-area switch, by the same rule and with the same exception.
  const forbiddenShown = appearance.forbiddenAreasVisible || activeTab === 'forbidden'
  const forbiddenVisibility = forbiddenShown ? 'visible' : 'none'

  const borderFC = useMemo(
    () => groundDisplayFeatureCollection(borderDisplay, 'border', { editingId: borderEditingId, vertexId: borderVertexId, teamColor }),
    [borderDisplay, borderEditingId, borderVertexId, teamColor],
  )
  const subareaFC = useMemo(
    () => groundDisplayFeatureCollection(subAreaDisplay, 'subarea', { editingId: areaEditingId, vertexId: subareaVertexId }),
    [subAreaDisplay, areaEditingId, subareaVertexId],
  )
  const forbiddenFC = useMemo(
    () => groundDisplayFeatureCollection(forbiddenDisplay, 'forbidden', { editingId: areaEditingId, vertexId: forbiddenVertexId }),
    [forbiddenDisplay, areaEditingId, forbiddenVertexId],
  )

  const hitFC = useMemo(() => {
    // Array order here is presentational only (it decides paint/z-order for
    // the OVERLAPPING hit-target strokes themselves, invisible as they are)
    // — it is NOT what makes border/forbidden/subarea click precedence
    // deterministic. That precedence is `EditorClickRouting`'s job: it runs
    // three separate, kind-filtered `queryRenderedFeatures` calls in a fixed
    // border → forbidden → subarea order and takes the first hit, rather
    // than trusting MapLibre's undocumented same-layer feature return order.
    const features = [
      // A sub-area the switch has turned off is not clickable either: an
      // invisible outline that still selects on click is a click the reader
      // cannot account for.
      ...(subareasShown ? groundHitFeatures(subAreaDisplay, 'subarea', { editingId: areaEditingId, vertexId: subareaVertexId }) : []),
      ...(forbiddenShown ? groundHitFeatures(forbiddenDisplay, 'forbidden', { editingId: areaEditingId, vertexId: forbiddenVertexId }) : []),
      ...(showParcels ? [] : groundHitFeatures(borderDisplay, 'border', { editingId: borderEditingId, vertexId: borderVertexId })),
    ]
    return { type: 'FeatureCollection' as const, features }
  }, [borderDisplay, subAreaDisplay, forbiddenDisplay, borderEditingId, borderVertexId, areaEditingId, subareaVertexId, forbiddenVertexId, showParcels, subareasShown, forbiddenShown])

  // The old canvas's border hit-target law has a second term the flat
  // GROUND_HIT_TARGET_CSS_PX alone doesn't capture: `max(21, coreWidth ×
  // GROUND_HALO_RATIO)` (see `renderBorderShape`'s `hitWidth` in the old
  // FullscreenMapCanvas) — a thick border (a high `lineThickness.border`
  // appearance setting) gets a hit target that grows past 21px so it never
  // becomes HARDER to click than its own visible stroke. Sub-area and
  // forbidden never had this second term in the old canvas at all — their
  // click target was always just the crisp edge's own (thin) stroke — so
  // they keep the flat floor only; widening them to match would be inventing
  // a law the reference implementation never had. `groundHitWidthExpression`
  // (mapGeoJson.ts) owns the actual expression shape — see its own doc
  // comment for why this can't just nest `groundWidthExpression`'s output
  // inside `max`/`match` the way this used to (a real `addLayer` rejects it;
  // the layer silently never existed, and every ground click-to-select with it).
  const groundHitWidth = groundHitWidthExpression(GROUND_BASE_WIDTH.hunt, lt.border)

  const subareaLabels = useMemo(() => {
    if (!subareasShown || !(zoom >= SUBAREA_LABEL_MIN_ZOOM)) return []
    return subAreaDisplay.flatMap((item) => {
      if (isAreaItemHidden(item, { editingId: areaEditingId, vertexId: subareaVertexId }) || !item.name) return []
      const centroid = polygonCentroidOfRings(item.rings)
      if (!centroid) return []
      return [{ key: item.key, name: item.name, centroid }]
    })
  }, [subAreaDisplay, areaEditingId, subareaVertexId, zoom, subareasShown])

  if (printMap) return <TeamMapLayers map={{ ...printMap, lines: [], objects: [] }} appearance={appearance} zoom={zoom} printMode dimOutside={dimOutside} />

  return (
    <>
      <GeoJsonLineLayer
        id={SUBAREA_LINE_ID}
        data={subareaFC}
        paint={{
          'line-color': SUBAREA_STROKE,
          'line-opacity': SUBAREA_STROKE_OPACITY * (subareaDim ? GROUND_DIM_FACTOR : 1),
          'line-width': groundWidthExpression(GROUND_BASE_WIDTH.subarea, lt.subarea),
        }}
        layout={{ visibility: subareasShown ? 'visible' : 'none' }}
      />
      <GeoJsonFillLayer
        id={FORBIDDEN_FILL_ID}
        data={forbiddenFC}
        paint={{ 'fill-color': FORBIDDEN_FILL, 'fill-opacity': forbiddenDim ? GROUND_DIM_FACTOR : 1 }}
        layout={{ visibility: forbiddenVisibility }}
      />
      <GeoJsonLineLayer
        id={FORBIDDEN_LINE_ID}
        data={forbiddenFC}
        paint={{
          'line-color': FORBIDDEN_STROKE,
          'line-opacity': forbiddenDim ? GROUND_DIM_FACTOR : 1,
          'line-width': groundWidthExpression(GROUND_BASE_WIDTH.forbidden, lt.forbidden),
        }}
        layout={{ visibility: forbiddenVisibility }}
      />
      <GeoJsonLineLayer
        id={BORDER_HALO_ID}
        data={borderFC}
        paint={{
          'line-color': ['get', 'color'],
          'line-opacity': GROUND_HALO_OPACITY * (borderDim ? GROUND_DIM_FACTOR : 1),
          'line-width': groundWidthExpression(GROUND_BASE_WIDTH.halo, lt.border),
          // Outside the ground, the same band the read-only team map draws;
          // `groundHaloOffsetExpression` owns why the sign is what it is.
          'line-offset': groundHaloOffsetExpression(GROUND_BASE_WIDTH.halo, lt.border),
        }}
        layout={{ visibility: shouldDrawGroundHalo(zoom) ? 'visible' : 'none' }}
      />
      <GeoJsonLineLayer
        id={BORDER_LINE_ID}
        data={borderFC}
        paint={{
          'line-color': ['get', 'color'],
          'line-opacity': borderDim ? GROUND_DIM_FACTOR : 1,
          'line-width': groundWidthExpression(GROUND_BASE_WIDTH.hunt, lt.border),
        }}
      />

      {/* selectedId emphasis — filtered to at most one feature per kind. */}
      <GeoJsonLineLayer
        id={SUBAREA_HIGHLIGHT_ID}
        data={subareaFC}
        paint={{ 'line-color': GROUND_HIGHLIGHT_COLOR, 'line-width': groundWidthExpression(GROUND_BASE_WIDTH.subarea, lt.subarea * 1.6) }}
        filter={['==', ['get', 'id'], selectedId ?? NO_SELECTION]}
        layout={{ visibility: subareasShown ? 'visible' : 'none' }}
      />
      <GeoJsonLineLayer
        id={FORBIDDEN_HIGHLIGHT_ID}
        data={forbiddenFC}
        paint={{ 'line-color': GROUND_HIGHLIGHT_COLOR, 'line-width': groundWidthExpression(GROUND_BASE_WIDTH.forbidden, lt.forbidden * 1.6) }}
        filter={['==', ['get', 'id'], selectedId ?? NO_SELECTION]}
        layout={{ visibility: forbiddenVisibility }}
      />
      <GeoJsonLineLayer
        id={BORDER_HIGHLIGHT_ID}
        data={borderFC}
        paint={{ 'line-color': GROUND_HIGHLIGHT_COLOR, 'line-width': groundWidthExpression(GROUND_BASE_WIDTH.hunt, lt.border * 1.6) }}
        filter={['==', ['get', 'id'], selectedId ?? NO_SELECTION]}
      />

      {/* The wide, invisible hit target the click router queries. */}
      {clickable && (
        <GeoJsonLineLayer
          id={GROUND_HIT_ID}
          data={hitFC}
          paint={{ 'line-color': 'transparent', 'line-width': groundHitWidth }}
        />
      )}

      {subareaLabels.map(({ key, name, centroid }) => (
        <MapMarker key={key} lat={centroid.lat} lon={centroid.lon} anchor="center">
          <span
            style={{
              fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 11,
              color: '#fff',
              whiteSpace: 'nowrap',
              textShadow: '0 0 2px #000, 0 0 2px #000, 0 0 2px #000',
              opacity: subareaDim ? LABEL_DIM_OPACITY : 1,
            }}
          >
            {name.toUpperCase()}
          </span>
        </MapMarker>
      ))}
    </>
  )
}

